import { useMutation } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { api, ApiError } from "@/api/client";
import { SyncRunsCard } from "@/components/sync-runs-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function SyncOneProductCard() {
  const [productId, setProductId] = useState("");
  const mutation = useMutation({
    mutationFn: (id: number) => api.triggerSync(id),
    onSuccess: (result) => {
      if (result.outcome === "already-running") {
        toast.info(`That product was already queued (job ${result.jobId})`);
      } else {
        toast.success(`Sync queued (job ${result.jobId})`);
      }
      setProductId("");
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiError ? error.message : "Could not queue the sync.",
      );
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const id = Number(productId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error("Enter a valid product ID.");
      return;
    }
    mutation.mutate(id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sync a product</CardTitle>
        <CardDescription>
          Queue or re-queue one product by its internal ID
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="productId">Product ID</Label>
            <Input
              id="productId"
              type="number"
              min={1}
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              placeholder="123"
            />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            <RefreshCw className={mutation.isPending ? "size-4 animate-spin" : "size-4"} />
            Sync
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function ShopifySyncPage() {
  return (
    <div className="space-y-4">
      <SyncOneProductCard />
      <SyncRunsCard />
    </div>
  );
}
